import axios, { AxiosRequestConfig } from "axios";
import { store } from "@State/store";
import { Snackbar } from "@State/actions/actionCreators";
import { AppActions } from "@State/reducers/getReducers/appReducer";
import { logout } from "@State/effects/security/logout";
import { getRedirectUrl } from "@View/pages/AdminPO/SigninPage";
import { getRandomKey } from "@Utils/math";
import { getAuthToken } from "@Security/stateHelpers";
import { REACT_APP_BACKEND_URL } from "@Constants/tokens";
import { refreshAccessToken } from "@State/effects/security/login";

const DEFAULT_ERROR_MESSAGE =
  "There was an error. Please try later or contact tech support.";

/**
 * Извлекает пользовательское сообщение об ошибке из тела ответа бэка.
 * Бэк отвечает по-разному: обёртка ApiResponse<T> (`{ success, data, error }`)
 * либо «сырой» объект (`{ message }`). Для не-JSON тел (HTML-страница 500 и т.п.)
 * возвращает "" — снекбар тогда падает на переданный errorText / дефолт.
 */
const extractErrorMessage = (error: any): string => {
  // Интерсептор при status >= 400 кидает error.response, поэтому тело обычно
  // лежит в error.data; на сетевых/иных ошибках — в error.response.data.
  const data = error?.data ?? error?.response?.data;
  if (!data) return "";
  if (typeof data === "string") {
    const text = data.trim();
    return text && !text.startsWith("<") ? text : "";
  }
  if (typeof data === "object") {
    const message = data.error || data.message;
    return typeof message === "string" ? message : "";
  }
  return "";
};

// Auth endpoints that should not trigger token refresh
const AUTH_ENDPOINTS = [
  "/api/Auth/Login",
  "/api/Auth/LoginByToken",
  "/api/Auth/RefreshToken",
  "/api/Customer/selfRegistration",
];

// Flag to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
// Queue of pending requests to retry after token refresh
let pendingRequests: Array<{
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}> = [];

const processPendingRequests = (token: string | null, error?: unknown) => {
  pendingRequests.forEach(({ resolve, reject }) => {
    if (token) {
      resolve(token);
    } else {
      reject(error);
    }
  });
  pendingRequests = [];
};

type TCreateAppAPI = {
  baseURL?: string;
};

export type TRequestConfig = AxiosRequestConfig & {
  suppressError?: boolean;
  suppressLoading?: boolean;
  suppressSnackbar?: boolean;
  successText?: string;
  errorText?: string;
  errorIfNotOk?: boolean;
  _fileSettings?: object;
};

const createAppAPI = (configs: TCreateAppAPI) => {
  const api = axios.create({
    baseURL: configs.baseURL,
  });

  api.interceptors.response.use(undefined, async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;
    const requestUrl = originalRequest?.url || "";

    // Skip refresh logic for auth endpoints
    if (AUTH_ENDPOINTS.some((endpoint) => requestUrl.includes(endpoint))) {
      return Promise.reject(error);
    }

    if (status === 401) {
      // If this request was already retried after refresh, don't try again
      if (originalRequest._retry) {
        return Promise.reject(error);
      }

      // If already refreshing, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          pendingRequests.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `bearer ${token}`;
            originalRequest._retry = true;
            return api.request(originalRequest);
          })
          .catch(() => {
            // Refresh failed, let the error propagate
            return Promise.reject(error);
          });
      }

      isRefreshing = true;
      originalRequest._retry = true;

      try {
        // Attempt to refresh the token
        const newToken = await refreshAccessToken();

        if (newToken) {
          // Token refreshed successfully, retry the original request
          originalRequest.headers.Authorization = `bearer ${newToken}`;
          processPendingRequests(newToken);
          isRefreshing = false;
          return api.request(originalRequest);
        }
      } catch (refreshError) {
        processPendingRequests(null, refreshError);
        isRefreshing = false;
        // Don't logout on refresh error, just reject the request
        return Promise.reject(error);
      }

      // Refresh returned null (no refresh token or invalid) - logout user
      isRefreshing = false;
      processPendingRequests(null, error);
      store.dispatch(
        Snackbar({ message: "snackbar.notAuth", open: true, severity: "error" })
      );
      await logout()(store.dispatch);
      window.location.reload();
      return Promise.reject(error);
    } else if (status === 403) {
      store.dispatch(
        Snackbar({ message: "snackbar.notPermitted", open: true, severity: "error" })
      );
      // window.location.replace(getRedirectUrl());
    } else if (status >= 400) {
      throw error.response;
    }
  });

  return {
    async request<T>({
      suppressError = false,
      suppressLoading = false,
      suppressSnackbar = false,
      successText = "",
      errorText = "",
      errorIfNotOk = true,
      method = "get",
      _fileSettings = {},
      ...config
    }: TRequestConfig): Promise<T> {
      let response = null;
      const LoadingKey = getRandomKey();
      if (!suppressLoading) store.dispatch(AppActions.startLoading(LoadingKey));

      try {
        response = await api.request<T>({
          method,
          ...config,
          headers: this.computeHeaders(config.headers || {}, config.data),
        });

        if (errorIfNotOk && !response) {
          throw new Error("API not OK response");
        }

        if (successText) {
          if (!suppressSnackbar)
            store.dispatch(
              Snackbar({ message: successText, open: true, severity: "success" })
            );
          store.dispatch(AppActions.setSuccess(successText));
        } else if (method.toLowerCase() !== "get") {
          if (!suppressSnackbar)
            store.dispatch(
              Snackbar({ message: "snackbar.success", open: true, severity: "success" })
            );
          store.dispatch(AppActions.setSuccess("snackbar.success"));
        }

        if (!suppressLoading) store.dispatch(AppActions.endLoading(LoadingKey));

        if (config.responseType === "blob" && "name" in _fileSettings) {
          const contentDisposition = response.headers["content-disposition"];
          const utf8FilenameRegex = /filename\*=UTF-8''([^;]+)(?:; ?|$)/i;
          const asciiFilenameRegex = /filename="([^"]+)"/i;
          // @ts-ignore
          _fileSettings.name = decodeURIComponent(
            utf8FilenameRegex.exec(contentDisposition ?? "")?.[1]?.trim() ??
            asciiFilenameRegex.exec(contentDisposition ?? "")?.[1] ??
            "report.xlsx"
          );
        }
      } catch (e) {
        if (!suppressLoading) store.dispatch(AppActions.endLoading(LoadingKey));

        console.warn(`<<< API error for request ${config.url} >>> `);
        console.error(e);

        // При responseType: "blob" тело ошибки тоже Blob — без разбора текст бэка теряется.
        const errorData = (e as any)?.data;
        if (errorData instanceof Blob && errorData.type.includes("json")) {
          try {
            (e as any).data = JSON.parse(await errorData.text());
          } catch (parseError) {
            console.error("[API] blob error parse", parseError);
          }
        }

        // Приоритет: сообщение из ответа бэка → переданный errorText → дефолт.
        const backendMessage = extractErrorMessage(e);
        const snackbarMessage = backendMessage || errorText || "snackbar.error";
        store.dispatch(
          AppActions.setError(backendMessage || errorText || DEFAULT_ERROR_MESSAGE)
        );
        if (!suppressSnackbar) {
          store.dispatch(
            Snackbar({ message: snackbarMessage, open: true, severity: "error" })
          );
        }
        if (!suppressError) {
          throw e;
        }
      }

      return response?.data as T;
    },

    computeHeaders(headers: object, data: any) {
      const token = getAuthToken();
      return {
        ...headers,
        ...(token ? { Authorization: `bearer ${token}` } : {}),
        ...(data instanceof FormData
          ? { "Content-Type": "multipart/form-data" }
          : {}),
      };
    },
  };
};

export const APP_WS_ENDPOINT = () => {
  const backendUrl = REACT_APP_BACKEND_URL();

  if (!backendUrl) {
    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    return `${protocol}//${window.location.host}`;
  }
  return backendUrl.replace("https", "wss").replace("http", "ws");
};

let AppAPI: ReturnType<typeof createAppAPI>;
const getAPI = () => {
  if (!AppAPI) {
    AppAPI = createAppAPI({
      baseURL: REACT_APP_BACKEND_URL(),
    });
  }
  return AppAPI;
};

export default getAPI;
